import React, { useState } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { DashboardCard } from "../../../../../shared/components";
import { fatigueService } from "../data/fatigueService";
import { useAsync } from "../data/useFatigueData";
import { METRIC_DEFINITIONS } from "../data/methodology";
import { KpiCard } from "../components/KpiCard";
import { FatigueTrendChart } from "../components/FatigueTrendChart";
import { CohortRiskTable } from "../components/CohortRiskTable";
import { WearCompliancePanel } from "../components/WearCompliancePanel";
import { MetricDefinitionDrawer } from "../components/MetricDefinitionDrawer";
import { SectionHeading } from "../components/primitives";
import type { DerivedMetricKey } from "../data/types";

interface OverviewSectionProps {
  onOpenCohort: (cohortId: string) => void;
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({ onOpenCohort }) => {
  const { data, loading } = useAsync(() => fatigueService.getOverview());
  const [drawerKey, setDrawerKey] = useState<DerivedMetricKey | null>(null);

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Executive summary"
        title="Fatigue Overview"
        subtitle="PEAK4-derived fatigue exposure across the workforce, versus baseline and the previous period."
      />

      {/* KPI cards */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
        }}
      >
        {loading || !data
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={220} />
            ))
          : data.metrics.map((m) => (
              <KpiCard
                key={m.key}
                metric={m}
                explanation={METRIC_DEFINITIONS[m.key].derivation}
                decimals={0}
                onClick={() => setDrawerKey(m.key)}
              />
            ))}
      </Box>

      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" } }}>
        <DashboardCard
          title="Derived indicator trend"
          subtitle="FED, LRM, CDI and PDC over the last 12 weeks."
          expandable={false}
        >
          {loading || !data ? (
            <Skeleton variant="rounded" height={300} />
          ) : (
            <FatigueTrendChart data={data.trend} height={300} />
          )}
        </DashboardCard>

        <DashboardCard
          title="Coverage & wear compliance"
          subtitle="Confidence in these figures depends on wearable data completeness."
          expandable={false}
        >
          {loading || !data ? (
            <Skeleton variant="rounded" height={300} />
          ) : (
            <Stack spacing={1.5}>
              <Stat label="Active users" value={`${data.coverage.activeUsers}/${data.coverage.enrolledUsers}`} />
              <Stat label="Coverage" value={`${data.coverage.coveragePct}%`} />
              <Stat label="Valid data days" value={`${data.coverage.validDataDays}`} />
              <Stat label="Data confidence" value={`${data.coverage.confidenceScore}/100`} />
            </Stack>
          )}
        </DashboardCard>
      </Box>

      <DashboardCard
        title="Top risk cohorts"
        subtitle="Highest fatigue exposure first. Select a cohort for contributing factors and controls."
        expandable={false}
      >
        {loading || !data ? (
          <Skeleton variant="rounded" height={180} />
        ) : (
          <CohortRiskTable rows={data.topRiskCohorts} onSelectCohort={onOpenCohort} />
        )}
      </DashboardCard>

      <MetricDefinitionDrawer
        open={drawerKey !== null}
        metricKey={drawerKey}
        onClose={() => setDrawerKey(null)}
      />
    </Stack>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={800}>
      {value}
    </Typography>
  </Stack>
);
